import React, { useEffect, useState } from 'react';
import TextField from '@material-ui/core/TextField';
import Autocomplete from '@material-ui/lab/Autocomplete';
import CircularProgress from '@material-ui/core/CircularProgress';
import { Theme, createStyles, makeStyles } from '@material-ui/core/styles';

import { API_CONFIG } from '../../../shared/api/config';
import composeRequest from '../../../shared/api/core';
import { MasterBrand } from '../../models/models';
import {
  formatMasterBrandLabel,
  isSameMasterBrand,
} from '../../lib/masterBrandDisplay';
import { renderMasterBrandOption } from './MasterBrandOptionRender';

function sleep(delay = 0) {
  return new Promise((resolve) => {
    setTimeout(resolve, delay);
  });
}

export interface MasterBrandMultiSelectProps {
  selected: MasterBrand[];
  onSelectChange: (values: MasterBrand[]) => void;
  data?: MasterBrand[];
  businessId?: string | number | null;
  url?: string;
  isResponsePaginated?: boolean;
  hasSelect?: boolean | null;
}

const BASE_URL = API_CONFIG.API_BASE_PATH;

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    listbox: {
      maxHeight: 180,
    },
  }),
);

const MasterBrandMultiSelect = (props: MasterBrandMultiSelectProps) => {
  const classes = useStyles({});
  const {
    selected,
    onSelectChange,
    data,
    businessId,
    url = 'korona/master/products-brand-list/',
    isResponsePaginated = true,
    hasSelect = true,
  } = props;

  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<MasterBrand[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const loading = open && options.length === 0 && !data;

  useEffect(() => {
    let active = true;

    if (data) {
      setOptions(data);
      return undefined;
    }

    if (!loading) {
      return undefined;
    }

    (async () => {
      try {
        let requestUrl = `${BASE_URL}${url}`;
        if (businessId) {
          requestUrl += `/?business_id=${businessId}`;
        }
        if (searchTerm) {
          requestUrl += `${requestUrl.includes('?') ? '&' : '?'}search=${encodeURIComponent(searchTerm)}`;
        }
        await sleep(500);
        const response = await composeRequest(requestUrl, 'GET', null);
        const responseData: { results?: MasterBrand[] } | MasterBrand[] = await response.json();
        if (active) {
          const optionsArray = isResponsePaginated
            ? (Array.isArray((responseData as { results?: MasterBrand[] }).results)
              ? (responseData as { results: MasterBrand[] }).results
              : [])
            : (Array.isArray(responseData) ? responseData : []);
          setOptions(optionsArray);
        }
      } catch (error) {
        console.error('Error fetching master brands:', error);
        if (active) {
          setOptions([]);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, [loading, searchTerm, data, businessId, url, isResponsePaginated]);

  useEffect(() => {
    if (!open && !data) {
      setOptions([]);
    }
  }, [open, data]);

  const handleChange = (_event: object, value: MasterBrand[]) => {
    onSelectChange(value);
  };

  const onInputChange = (_event: object, value: string) => {
    setSearchTerm(value ? value.trim() : '');
  };

  if (hasSelect === false) {
    return null;
  }

  return (
    <Autocomplete
      multiple
      size="small"
      classes={{ listbox: classes.listbox }}
      style={{ width: '100%' }}
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      filterSelectedOptions
      value={selected}
      getOptionSelected={(option, value) => isSameMasterBrand(option, value)}
      getOptionLabel={(option) => formatMasterBrandLabel(option)}
      onChange={handleChange}
      onInputChange={onInputChange}
      options={options}
      loading={loading}
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <React.Fragment>
                {loading ? <CircularProgress color="primary" size={20} /> : null}
                {params.InputProps.endAdornment}
              </React.Fragment>
            ),
          }}
        />
      )}
      renderOption={(option, { inputValue }) =>
        renderMasterBrandOption(option, inputValue)
      }
    />
  );
};

export default MasterBrandMultiSelect;
